import { useCustom } from '@refinedev/core';
import { useCallback } from 'react';

/**
 * The CLIENT's era for one domain (plan 07 C3 — the portal routes by the client's mode, never the
 * therapist's). Both signal queries are asked fresh on every mount and re-asked when the server
 * refuses a write with a coexistence code (HANDOFF T112: the era is a server fact that can change
 * while the page is open; a cached signal is what stranded the E12 pilot).
 *
 * `esReadyDomains(subject)` is personalised per subject — a PILOT domain counts as ready only for
 * the listed accounts — so asking for the therapist (the caller) would never show a client's pilot.
 */
/**
 * The client as this hook reads them. `accessLevel` + `access` are here for the EDIT GATE
 * (`canEditExperiment`): asked per client, this query can never answer "unknown client" the way a
 * paginated `clients` page does, which is how the view and the list came to disagree about who may
 * be edited (founder QA 2026-09-02, defect A).
 */
export type EraClient = {
  _id: string;
  esMode?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  accessLevel?: string | null;
  access?: {
    allowEdit?: boolean | null;
    allowAdminEdit?: boolean | null;
  } | null;
};

export type ClientEra = {
  /** `es` AND the domain is ready for this client — the ES commands are the path. */
  isEsClient: boolean;
  esMode: string | null;
  readyDomains: string[];
  /** Both signals have answered (or no client is selected yet). */
  eraLoaded: boolean;
  client: EraClient | null;
  refetchEra: () => Promise<void>;
};

export function useClientEra(
  clientId: string | undefined,
  domain: string,
): ClientEra {
  const { data: clientData, refetch: refetchClient } = useCustom<EraClient>({
    url: '',
    method: 'get',
    meta: {
      operation: 'client',
      fields: [
        '_id',
        'esMode',
        'firstName',
        'lastName',
        'accessLevel',
        { access: ['allowEdit', 'allowAdminEdit'] },
      ],
      variables: { id: { name: 'id', type: 'ID', value: clientId } },
    },
    queryOptions: {
      enabled: Boolean(clientId),
      staleTime: 0,
      refetchOnMount: 'always',
    },
  });
  const { data: readyData, refetch: refetchReady } = useCustom<string[]>({
    url: '',
    method: 'get',
    meta: {
      operation: 'esReadyDomains',
      variables: {
        subject: { name: 'subject', type: 'ID', value: clientId },
      },
    },
    queryOptions: {
      enabled: Boolean(clientId),
      staleTime: 0,
      refetchOnMount: 'always',
    },
  });

  const readyDomains =
    (readyData?.data as unknown as string[] | undefined) ?? [];
  const esMode = clientData?.data?.esMode ?? null;

  const refetchEra = useCallback(async () => {
    await Promise.all([refetchClient(), refetchReady()]);
  }, [refetchClient, refetchReady]);

  return {
    isEsClient: esMode === 'es' && readyDomains.includes(domain),
    esMode,
    readyDomains,
    eraLoaded: clientId
      ? clientData !== undefined && readyData !== undefined
      : true,
    client: clientData?.data ?? null,
    refetchEra,
  };
}
